<script>
  import { router } from '@inertiajs/svelte'
  import AddressSelector from '../AddressSelector/index.svelte'
  
  let { 
    property = null, 
    property_types, 
    spaces = [], 
    addresses = [],
    submitUrl,
    submitMethod = 'post',
    onSuccess,
    actions
  } = $props()
  
  let form = $state({
    name: property?.name || '',
    property_type: property?.property_type || Object.keys(property_types)[0] || 'house',
    square_meters: property?.square_meters || null,
    year_built: property?.year_built || null,
    notes: property?.notes || '',
    spaces_attributes: []
  })
  
  // Initialize space array with existing spaces, only add empty space if no spaces exist
  let allSpaces = $state([
    ...spaces.map(space => ({ 
      id: space.id, 
      name: space.name || '', 
      description: space.description || '',
      isExisting: true
    })),
    ...(spaces.length === 0 ? [{ name: '', description: '', isExisting: false }] : [])
  ])
  
  let errors = $state({})
  let processing = $state(false)
  let addressSelector
  
  // Effect to update form when property changes
  $effect(() => {
    if (property) {
      form = {
        name: property.name || '',
        property_type: property.property_type || Object.keys(property_types)[0] || 'house',
        square_meters: property.square_meters || null,
        year_built: property.year_built || null,
        notes: property.notes || '',
        spaces_attributes: []
      }
    }
  })
  
  function handleSubmit(event) {
    event.preventDefault();
    processing = true
    
    // Process spaces - update existing, add new, mark for deletion
    form.spaces_attributes = allSpaces
      .filter(space => space.name.trim() || (space.isExisting && space._destroy))
      .map(space => {
        const spaceData = { 
          name: space.name.trim(), 
          description: space.description.trim() 
        }
        
        // Add ID for existing spaces
        if (space.isExisting) {
          spaceData.id = space.id
          
          // Mark for deletion if flagged
          if (space._destroy) {
            spaceData._destroy = '1'
          }
        }
        
        return spaceData
      })
    
    // Get address data from the selector component
    const addressData = addressSelector.getAddressData()
    
    // Merge the address data into the form
    const formData = { ...form, ...addressData }
    
    const options = {
      onSuccess: () => {
        processing = false
        if (onSuccess) onSuccess()
      },
      onError: (e) => {
        errors = e
        processing = false
      }
    }

    if (submitMethod === 'put') {
      router.put(submitUrl, formData, options)
    } else {
      router.post(submitUrl, formData, options)
    }
  }
  
  function addSpace() {
    allSpaces = [...allSpaces, { name: '', description: '', isExisting: false }]
  }
  
  function removeSpace(index) {
    const space = allSpaces[index]
    
    if (space.isExisting) {
      // Mark existing spaces for deletion rather than removing them from the array
      allSpaces[index]._destroy = true
    } else {
      // Remove new spaces from the array
      allSpaces = allSpaces.filter((_, i) => i !== index)
    }
    
    // Always have at least one space input
    if (allSpaces.length === 0 || allSpaces.every(s => s._destroy)) {
      allSpaces = [...allSpaces.filter(s => !s._destroy), { name: '', description: '', isExisting: false }]
    }
  }
</script>

<form onsubmit={handleSubmit} class="bg-white shadow-md rounded-lg p-6">
  <div class="mb-4">
    <label for="name" class="block text-gray-700 font-medium mb-2">Property Name*</label>
    <input 
      type="text" 
      id="name" 
      bind:value={form.name} 
      class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
      placeholder="e.g. My House"
    />
    {#if errors.name}
      <p class="text-red-500 text-sm mt-1">{errors.name}</p>
    {/if}
  </div>
  
  <div class="mb-4">
    <label for="property_type" class="block text-gray-700 font-medium mb-2">Property Type</label>
    <select 
      id="property_type" 
      bind:value={form.property_type} 
      class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
    >
      {#each Object.entries(property_types) as [value, label]}
        <option value={value}>{label}</option>
      {/each}
    </select>
  </div>
  
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
    <div>
      <label for="square_meters" class="block text-gray-700 font-medium mb-2">Size (m²)</label>
      <input 
        type="number" 
        id="square_meters" 
        bind:value={form.square_meters} 
        class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        min="0"
      />
    </div>
    
    <div>
      <label for="year_built" class="block text-gray-700 font-medium mb-2">Year Built</label>
      <input 
        type="number" 
        id="year_built" 
        bind:value={form.year_built} 
        class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        min="1800"
        max={new Date().getFullYear()}
        placeholder="Year only (e.g. 1990)"
      />
    </div>
  </div>
  
  <div class="mb-4">
    <label for="notes" class="block text-gray-700 font-medium mb-2">Notes</label>
    <textarea 
      id="notes" 
      bind:value={form.notes} 
      class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
      rows="4"
    ></textarea>
  </div>
  
  <!-- Address Selector Component -->
  <AddressSelector 
    bind:this={addressSelector}
    addresses={addresses}
    address={property?.address}
    selectedAddressId={property?.address?.id}
    errors={errors}
  />
  
  <!-- Spaces Section -->
  <div class="mb-6 mt-8">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-lg font-semibold">Spaces</h2>
      <button 
        type="button" 
        onclick={addSpace}
        class="text-brand-700 hover:text-brand-800 font-medium text-sm flex items-center"
      >
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Add Space
      </button>
    </div>
    
    <div class="space-y-4">
      {#each allSpaces as space, index}
        <!-- Skip spaces marked for deletion -->
        {#if !space._destroy}
          <div class="border border-gray-200 rounded-md p-4 bg-gray-50">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-md font-medium">
                {space.isExisting ? `Space: ${space.name}` : `New Space ${index + 1}`}
              </h3>
              <button 
                type="button" 
                onclick={() => removeSpace(index)}
                class="text-red-600 hover:text-red-800 text-sm"
              >
                Remove
              </button>
            </div>
            <div class="mb-3">
              <label for="space-name-{index}" class="block text-gray-700 font-medium mb-1 text-sm">Space Name*</label>
              <input 
                type="text" 
                id="space-name-{index}" 
                bind:value={space.name} 
                class="w-full border-gray-300 rounded-md shadow-sm px-3 py-1.5 text-sm focus:ring-brand-600 focus:border-brand-600"
                placeholder="e.g. Living Room"
              />
              {#if errors[`spaces_attributes.${index}.name`]}
                <p class="text-red-500 text-sm mt-1">Space name {errors[`spaces_attributes.${index}.name`]}</p>
              {/if}
            </div>
            
            <div>
              <label for="space-description-{index}" class="block text-gray-700 font-medium mb-1 text-sm">Description</label>
              <textarea 
                id="space-description-{index}" 
                bind:value={space.description} 
                class="w-full border-gray-300 rounded-md shadow-sm px-3 py-1.5 text-sm focus:ring-brand-600 focus:border-brand-600"
                rows="2"
              ></textarea>
            </div>
          </div>
        {/if}
      {/each}
      
      {#if allSpaces.every(s => s._destroy)}
        <div class="text-center py-4 text-gray-500">
          No spaces available. Click "Add Space" to create one.
        </div>
      {/if}
    </div>
  </div>
  
  <div class="flex justify-end mt-6">
    {#if actions}
      {@render actions()}
    {:else}
      <button
        type="submit"
        class="bg-brand-700 hover:bg-brand-800 text-white font-bold py-2 px-4 rounded"
        disabled={processing}
      >
        {processing ? 'Saving...' : 'Save Property'}
      </button>
    {/if}
  </div>
</form> 